/**
 * Toggle — a reusable on/off switch (controlled).
 * Use anywhere a boolean is set by flipping a switch rather than ticking a checkbox,
 * e.g. attendance present/absent. Accessible (role="switch", keyboard, aria-checked).
 */
import React from 'react';
import { cn } from '../../utils/cn';

interface ToggleProps {
	checked: boolean;
	onChange: (checked: boolean) => void;
	disabled?: boolean;
	/** Accessible label (screen readers); visual label is up to the caller. */
	'aria-label'?: string;
	className?: string;
}

export const Toggle: React.FC<ToggleProps> = ({
	checked,
	onChange,
	disabled = false,
	className,
	...rest
}) => {
	return (
		<button
			type='button'
			role='switch'
			aria-checked={checked}
			aria-label={rest['aria-label']}
			disabled={disabled}
			onClick={() => !disabled && onChange(!checked)}
			className={cn(
				'relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1',
				checked ? 'bg-green-600' : 'bg-gray-300',
				disabled && 'cursor-not-allowed opacity-50',
				className
			)}
		>
			<span
				className={cn(
					'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
					checked ? 'translate-x-4' : 'translate-x-0.5'
				)}
			/>
		</button>
	);
};

export default Toggle;
